import { Box, Grid, Typography, makeStyles, Theme, CircularProgress } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useState, useEffect } from 'react';
import { CustomTaskDialog } from '@roc/feature-general-tasks';
import { Button, StatusDiv } from '@roc/ui';
import { useStore } from '@roc/client-portal-shared/hooks';
import { DocReviewSection } from './docReviewSection';
import { DocReviewTableResolver, DocReviewPreSection, DocReviewPostSection, DocReviewUploaderResolver, docReviewSectionLayout } from './docReviewTableResolver';
import { DocReviewEditModal } from './genericDocReview/docReviewEditModal';
import { emptyTableText } from '../utils/utils';

const useStyles = makeStyles((theme: Theme) => ({
  emptyContent: {
    padding: theme.spacing(3, 2),
    minHeight: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

export interface DocReviewDialogV2Props {
  open: boolean;
  handleClose: () => void;
  taskId: number;
}

export interface DocReviewContentProps {
  taskId: number;
}

export const DocReviewContent = observer(({ taskId }: DocReviewContentProps) => {
  const classes = useStyles();
  const { docReviewStoreV2, loanStore } = useStore();
  const isInternal = docReviewStoreV2.globalStore.isInternalPortal;
  const [editModalOpen, setEditModalOpen] = useState(false);
  const document = docReviewStoreV2.document;
  const loanDetails = loanStore?.loanDetails?.loanId === document?.loanId ? loanStore.loanDetails : null;
  const canEdit = docReviewStoreV2.canShowEditButton(document, loanDetails?.status);

  const onEditClick =
    docReviewStoreV2.processingStatus != 'IN_PROGRESS' && canEdit
      ? () => setEditModalOpen(true)
      : undefined;

  return (
    <Grid container spacing={2}>
      {docReviewStoreV2.showUploader(document) && (
        <DocReviewUploaderResolver documentName={document?.taskName ?? ''} />
      )}
      {docReviewStoreV2.showStatus && (
        <Grid item xs={12}>
          <StatusDiv
            status={docReviewStoreV2.processingStatus}
            text={docReviewStoreV2.statusText}
            onRefresh={
              isInternal
                ? () => docReviewStoreV2.getReviewTaskData(document.taskId)
                : undefined
            }
          />
        </Grid>
      )}
      {docReviewStoreV2.showPreSection && (
        <Grid item xs={12}>
          <DocReviewPreSection
            documentName={document?.taskName ?? ''}
            data={docReviewStoreV2.docReviewData}
          />
        </Grid>
      )}
      {docReviewStoreV2.showReviewSection && (
        <Grid item xs={12}>
          <DocReviewSection
            onEditClick={onEditClick}
            editModal={
              canEdit && (
                <DocReviewEditModal
                  open={editModalOpen}
                  onClose={() => setEditModalOpen(false)}
                  document={document}
                />
              )
            }
            hideEdit={!canEdit}
            postSection={
              docReviewStoreV2.hasReviewData && (
                <DocReviewPostSection
                  documentName={document?.taskName ?? ''}
                  data={docReviewStoreV2.docReviewData}
                />
              )
            }
            {...docReviewSectionLayout(document?.taskName ?? '')}
          >
            {docReviewStoreV2.hasReviewData ? (
              <DocReviewTableResolver
                documentName={document?.taskName ?? ''}
                data={docReviewStoreV2.docReviewData}
              />
            ) : (
              <Box className={classes.emptyContent}>
                <Typography className={classes.emptyText}>
                  {emptyTableText[docReviewStoreV2.processingStatus] ??
                    'No data extracted yet. Upload a document to get started.'}
                </Typography>
              </Box>
            )}
          </DocReviewSection>
        </Grid>
      )}
    </Grid>
  );
});

export const DocReviewDialogV2 = observer(({
  open,
  handleClose,
  taskId,
}: DocReviewDialogV2Props) => {
  const { docReviewStoreV2 } = useStore();
  const document = docReviewStoreV2.document;

  useEffect(() => {
    if (!open || !taskId) return;
    docReviewStoreV2.getReviewTaskData(taskId);
  }, [open, taskId]);

  const onClose = () => {
    docReviewStoreV2.reset();
    handleClose();
  };

  const dialogActions = (
    <Grid container justifyContent="flex-end">
      <Grid item>
        <Button
          onClick={onClose}
          color="primary"
          variant="outlined"
          testId="close"
        >
          Close
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <>
      {taskId && (
        <CustomTaskDialog
          open={open}
          maxWidth="md"
          title={document?.title}
          taskId={taskId}
          allowVertScroll={false}
          handleClose={onClose}
          dialogContent={<DocReviewContent taskId={taskId} />}
          dialogActions={dialogActions}
        />
      )}
    </>
  );
});
